import { createImageUrlBuilder, type SanityImageSource } from '@sanity/image-url'
import { dataset, projectId } from "@/sanity/lib/api";
import { stegaClean } from "next-sanity";

const imageBuilder = createImageUrlBuilder({
  projectId: projectId || "",
  dataset: dataset || "",
});

// Narrow SanityImageSource to the shape we actually handle
type ImageWithRef = Extract<SanityImageSource, { asset?: { _ref?: string } }>;

function hasAssetRef(source: SanityImageSource): source is ImageWithRef {
  return (
    typeof source === "object" &&
    source !== null &&
    "asset" in source &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Boolean((source as any).asset?._ref)
  );
}

export const urlForImage = (source: SanityImageSource) => {
  // Ensure that source image contains a valid reference
  if (!hasAssetRef(source)) {
    return undefined;
  }

  return imageBuilder.image(source).auto("format").fit("max");
};

export function resolveOpenGraphImage(image: any, width = 1200, height = 627) {
  if (!image) return;
  const url = urlForImage(image)?.width(width).height(height).fit("crop").url();
  if (!url) return;
  return { url, alt: image?.alt as string, width, height };
}

export function resolveHref(
  documentType?: string,
  slug?: string,
): string | undefined {
  switch (documentType) {
    case "homepage":
      return "/";

    case "page":
      return slug ? `/${slug}` : undefined;

    case "post":
      return slug ? `/posts/${slug}` : undefined;

    default:
      console.warn("Invalid document type:", documentType);
      return undefined;
  }
}

export function deepStegaClean<T>(value: T): T {
  if (typeof value === 'string') return stegaClean(value) as unknown as T;

  if (Array.isArray(value)) return value.map(deepStegaClean) as unknown as T;

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [key, deepStegaClean(val)])
    ) as T;
  }

  return value;
}
